/**
 * Artifact Deck (P3) — client-side slide helpers. An artifact STAYS markdown; a
 * doc using `---` slide separators is what the canvas 演示 mode renders and what
 * export turns into a deck. Nothing is stored as a binary — export converts on
 * demand. Two representations of the SAME markdown:
 *  - render: Marp core (@marp-team/marp-core) → styled slide HTML (in-canvas + a
 *    standalone HTML download), lazy-loaded so it never bloats the main bundle.
 *  - PPTX: pptxgenjs maps a lightweight parsed slide model (title + bullets/text)
 *    to a NATIVE, editable deck (text boxes, not screenshots) — the user can tweak
 *    it in PowerPoint. Its look intentionally differs from the Marp render.
 */

/** One line of slide body: a bullet (with indent depth) or a plain text line. */
export interface SlideBlock {
  kind: "bullet" | "text";
  text: string;
  /** Bullet indent depth (0 = top level). */
  level: number;
}

/** A parsed slide: an optional title (first heading) + body blocks. */
export interface Slide {
  title: string;
  blocks: SlideBlock[];
}

/** Strip inline markdown emphasis/code/link syntax to plain text (for PPTX). */
function stripInline(s: string): string {
  return s
    .replace(/`([^`]+)`/g, "$1") // `code`
    .replace(/\*\*([^*]+)\*\*/g, "$1") // **bold**
    .replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, "$1") // *italic*
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1") // [text](url)
    .trim();
}

/**
 * Parse markdown into slides, split on lines that are exactly `---` (the Marp /
 * common slide separator). Each slide's first heading becomes its title; `-`/`*`/`+`
 * lines become bullets (indent → level); other non-empty, non-structural lines
 * become text. Empty slides are dropped. A doc with no `---` is a single slide.
 */
export function parseSlides(markdown: string): Slide[] {
  const slides: Slide[] = [];
  let cur: Slide = { title: "", blocks: [] };
  let inFence = false;
  const flush = (): void => {
    if (cur.title.length > 0 || cur.blocks.length > 0) slides.push(cur);
    cur = { title: "", blocks: [] };
  };
  for (const raw of markdown.replace(/\r\n/g, "\n").split("\n")) {
    const line = raw;
    const trimmed = line.trim();
    if (trimmed.startsWith("```")) {
      inFence = !inFence;
      continue; // fence markers themselves are not slide content
    }
    if (!inFence && trimmed === "---") {
      flush();
      continue;
    }
    if (inFence) {
      if (trimmed.length > 0) cur.blocks.push({ kind: "text", text: line, level: 0 });
      continue;
    }
    const heading = /^(#{1,6})\s+(.*)$/.exec(trimmed);
    if (heading) {
      const text = stripInline(heading[2]!);
      if (cur.title.length === 0) cur.title = text;
      else cur.blocks.push({ kind: "text", text, level: 0 });
      continue;
    }
    const bullet = /^(\s*)[-*+]\s+(.*)$/.exec(line);
    if (bullet) {
      const level = Math.min(4, Math.floor(bullet[1]!.replace(/\t/g, "  ").length / 2));
      cur.blocks.push({ kind: "bullet", text: stripInline(bullet[2]!), level });
      continue;
    }
    if (trimmed.length > 0 && !/^([-*_])\1{2,}$/.test(trimmed)) {
      cur.blocks.push({ kind: "text", text: stripInline(trimmed), level: 0 });
    }
  }
  flush();
  return slides;
}

/** Count of slides a markdown doc yields (for the 演示 toggle label / guards). */
export function slideCount(markdown: string): number {
  return parseSlides(markdown).length;
}

/** Render markdown to Marp slide HTML + CSS (lazy import). */
export async function renderMarp(markdown: string): Promise<{ html: string; css: string }> {
  const { Marp } = await import("@marp-team/marp-core");
  const marp = new Marp({ html: false });
  const { html, css } = marp.render(markdown);
  return { html, css };
}

/** Build a standalone HTML document (self-contained) for the 导出 HTML download. */
export async function slidesToStandaloneHtml(markdown: string, title: string): Promise<string> {
  const { html, css } = await renderMarp(markdown);
  const safeTitle = title.replace(/[<>&]/g, "");
  return `<!doctype html>\n<html><head><meta charset="utf-8"><title>${safeTitle}</title>\n<style>${css}</style></head>\n<body>${html}</body></html>`;
}

/**
 * Map parsed slides to a native, editable PPTX (pptxgenjs, lazy import). Title is a
 * bold header; bullets render as an indented list; plain text as paragraphs. Returns
 * a Blob for download. Empty deck → a single title slide so export never fails.
 */
export async function slidesToPptxBlob(markdown: string, deckTitle: string): Promise<Blob> {
  const pptxgen = (await import("pptxgenjs")).default;
  const pptx = new pptxgen();
  pptx.layout = "LAYOUT_16x9";
  const slides = parseSlides(markdown);
  const list = slides.length > 0 ? slides : [{ title: deckTitle, blocks: [] as SlideBlock[] }];
  for (const s of list) {
    const slide = pptx.addSlide();
    if (s.title.length > 0) {
      slide.addText(s.title, { x: 0.5, y: 0.3, w: 9, h: 0.9, fontSize: 28, bold: true, color: "1F2937" });
    }
    const body = s.blocks.filter((b) => b.text.trim().length > 0);
    if (body.length > 0) {
      slide.addText(
        body.map((b) => ({
          text: b.text,
          options:
            b.kind === "bullet"
              ? { bullet: { indent: 15 }, indentLevel: b.level, fontSize: 16, color: "374151" }
              : { fontSize: 16, color: "374151", paraSpaceAfter: 6 },
        })),
        { x: 0.6, y: 1.4, w: 8.8, h: 3.8, valign: "top" },
      );
    }
  }
  return (await pptx.write({ outputType: "blob" })) as Blob;
}

/** Trigger a browser download of a Blob/string as `filename`. */
export function downloadBlob(data: Blob | string, filename: string, mime: string): void {
  const blob = typeof data === "string" ? new Blob([data], { type: mime }) : data;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
